/**
 * whatsappMessaging.service.ts — what the Comm Log composer needs to send an
 * approved WhatsApp message.
 *
 * Distinct from whatsappAdmin.service.ts, which configures the practice's
 * sender. This is used while messaging a patient.
 *
 * WHY APPROVED MESSAGES EXIST AT ALL: WhatsApp only carries a freely-written
 * message from a business for a day after the patient writes to them. After
 * that the only thing that will be delivered is a message WhatsApp has approved
 * in advance — and one of those may only be sent to a patient who agreed to be
 * contacted there, which is what the consent calls below are for.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '../types/api.types';

export interface WhatsAppTemplate {
  /** Provider id for the approved message; sent back as `contentSid`. */
  id: string;
  /** What staff pick from — the approved message's name, or its id if unnamed. */
  name: string;
  language: string;
  /** The text, with `{{1}}`-style placeholders still in it. */
  body: string;
  /** Placeholder keys in the order they appear, so the form can be built. */
  variables: string[];
}

export interface WhatsAppTemplateList {
  templates: WhatsAppTemplate[];
  /**
   * The practice has messages submitted but not yet approved. Only ever true
   * when we actually reached the provider — it is a real answer, never inferred
   * from a failure.
   */
  awaitingApproval: boolean;
  /**
   * THREE-STATE, and the distinction matters. `ok: false` means we could not
   * reach a verdict, NOT that the practice has no approved messages. Rendering
   * a provider blip as "you have none" would send staff off to re-create
   * messages they already have.
   */
  ok: boolean;
  error: 'credentials' | 'unreachable' | null;
}

export interface WhatsAppConsent {
  /** The patient agreed to be messaged on WhatsApp. */
  optedIn: boolean;
  /** How it was obtained, in plain words. Null when nothing is on file. */
  source: string | null;
  recordedAt: string | null;
  /**
   * The staff member who recorded it. Null when the patient's own message was
   * the evidence and nobody asserted anything on their behalf.
   */
  recordedBy: string | null;
}

export function getWhatsAppTemplates(): Promise<WhatsAppTemplateList> {
  return apiClient
    .get<ApiResponse<WhatsAppTemplateList>>('/communications/whatsapp/templates')
    .then((r) => r.data.data);
}

export function getWhatsAppConsent(patientId: number | string): Promise<WhatsAppConsent> {
  return apiClient
    .get<ApiResponse<WhatsAppConsent>>(`/communications/whatsapp/consent/${patientId}`)
    .then((r) => r.data.data);
}

/**
 * Record that the patient agreed, or withdraw it with `optedIn: false`.
 *
 * Recording agreement requires `confirmed` — the server refuses without it, so
 * consent cannot be set as a side effect of some other action. Withdrawing does
 * not: the safe direction is off, and putting a hurdle in front of honouring
 * "stop messaging me" would be backwards.
 */
export function setWhatsAppConsent(
  patientId: number | string,
  optedIn: boolean,
  confirmed = false,
): Promise<WhatsAppConsent> {
  return apiClient
    .post<ApiResponse<WhatsAppConsent>>(`/communications/whatsapp/consent/${patientId}`, {
      optedIn,
      confirmed,
    })
    .then((r) => r.data.data);
}
